/**
 * Tiny formatting helpers shared across pages. Keep this file dependency-free
 * — anything Intl-heavy belongs in its own module.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A bare calendar date ("2027-03-31"), as the API sends dates without a time. */
const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse an ISO-8601 string. A bare calendar date is read as that day in the
 * reader's time zone: `new Date('2027-03-31')` is UTC midnight, which shows
 * as 30 Mar anywhere west of UTC.
 */
function parseIso(iso: string): Date {
  const m = CALENDAR_DATE_RE.exec(iso);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(iso);
}

function dayMonthYear(d: Date): string {
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** Format an ISO-8601 string as a short local date and time: "31 Mar 2027, 14:05". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseIso(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dayMonthYear(d)}, ${hh}:${mm}`;
}

/**
 * Format an ISO-8601 string as a date: "31 Mar 2027". The one date format of
 * the app (C2-45). Built by hand rather than with `toLocaleDateString`, which
 * prints "Mar 31, 2027" or "31 Mar 2027" depending on the browser's locale,
 * and "Sep" or "Sept" depending on the runtime.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseIso(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dayMonthYear(d);
}

/** The short form chart axes use: "31 Mar". Same reading of the date as `formatDate`. */
export function formatDayMonth(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = parseIso(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** The short month name of a local time ("Mar"), as the month axes print it. */
export function formatMonth(ms: number): string {
  return MONTHS[new Date(ms).getMonth()] ?? '';
}

/**
 * Milliseconds since the epoch for an ISO date or instant, read the way
 * `formatDate` reads it (a bare calendar date is that day in the reader's
 * time zone), so a chart places a point on the day its label prints.
 */
export function dateValue(iso: string): number {
  return parseIso(iso).getTime();
}

/**
 * How long ago an ISO-8601 instant was, in plain words: "just now", "3 min
 * ago", "2 hours ago", "yesterday", "4 days ago", then the date.
 * `now` is a parameter so the words can be tested without a clock.
 */
export function formatRelative(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return formatDate(iso);
}

/**
 * Words that stay upper case when an identifier is title-cased, so a
 * currency or a contract term never reads "Usd" or "Sow" (C2-33). Only
 * unambiguous acronyms: "it" or "co" are ordinary words too often.
 */
const ACRONYMS: ReadonlyMap<string, string> = new Map(
  [
    'AI',
    'API',
    'CSV',
    'EDL',
    'ERP',
    'ID',
    'IV&V',
    'KPI',
    'PDF',
    'PMO',
    'QA',
    'RAID',
    'RFI',
    'RFP',
    'SIT',
    'SLA',
    'SOW',
    'UAT',
    'USD',
  ].map((a) => [a.toLowerCase(), a]),
);

/** The canonical spelling of an acronym ("usd" → "USD"), or null. */
export function acronym(word: string): string | null {
  return ACRONYMS.get(word.toLowerCase()) ?? null;
}

/** Pretty-print a Status string (e.g. `setup` → `Setup`, `usd` → `USD`). */
export function titleCase(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => acronym(w) ?? (w[0] ?? '').toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
